import { z } from "zod";
import { CONTACT_IMPORT_BIRTHDAY_FORMATS, } from "./contact-import-birthday.js";
import { parseCsv } from "./csv-parse.js";
/** Headers a canonical file must contain. Older 6-column files stay valid. */
export const CONTACT_IMPORT_CSV_HEADERS = [
    "first_name",
    "last_name",
    "display_name",
    "email",
    "phone",
    "groups",
];
/** Canonical headers that may be present but are never required. */
export const CONTACT_IMPORT_OPTIONAL_CSV_HEADERS = ["birthday"];
/** Downloadable template column order. */
export const CONTACT_IMPORT_TEMPLATE_HEADERS = [
    "first_name",
    "last_name",
    "display_name",
    "email",
    "phone",
    "birthday",
    "groups",
];
export const CONTACT_IMPORT_MAX_DATA_ROWS = 5000;
export const CONTACT_IMPORT_JOB_MAX_DATA_ROWS = 50_000;
export const CONTACT_IMPORT_JOB_MAX_FILE_BYTES = 15 * 1024 * 1024;
export const CONTACT_IMPORT_JOB_WRITE_BATCH_SIZE = 250;
export const CONTACT_IMPORT_JOB_PREVIEW_SAMPLE_ROWS = 20;
export const CONTACT_IMPORT_JOB_TTL_HOURS = 48;
export const CONTACT_IMPORT_COLUMN_TARGETS = [
    "firstName",
    "lastName",
    "displayName",
    "email",
    "phone",
    "birthday",
    "groups",
];
export const CONTACT_IMPORT_MAPPED_TARGETS = [...CONTACT_IMPORT_COLUMN_TARGETS, "skip"];
export const contactImportColumnMappingEntrySchema = z.object({
    source: z.string().trim().min(1).max(256),
    target: z.enum(CONTACT_IMPORT_MAPPED_TARGETS),
});
export const contactImportColumnMappingSchema = z
    .array(contactImportColumnMappingEntrySchema)
    .max(64);
const storedContactImportColumnMappingSchema = z.object({
    entries: contactImportColumnMappingSchema,
    birthdayFormat: z.enum(CONTACT_IMPORT_BIRTHDAY_FORMATS).optional(),
});
/**
 * `columnMapping` JSON is either the historical array or `{ entries, birthdayFormat }`.
 * An array means Auto.
 */
export function parseStoredContactImportColumnMapping(raw) {
    if (Array.isArray(raw)) {
        const entries = contactImportColumnMappingSchema.safeParse(raw);
        if (!entries.success)
            return null;
        return { entries: entries.data, birthdayFormat: "auto" };
    }
    const stored = storedContactImportColumnMappingSchema.safeParse(raw);
    if (!stored.success)
        return null;
    return {
        entries: stored.data.entries,
        birthdayFormat: stored.data.birthdayFormat ?? "auto",
    };
}
export class ContactImportParseError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "ContactImportParseError";
        this.code = code;
    }
}
const CANONICAL_HEADER_TO_TARGET = {
    first_name: "firstName",
    last_name: "lastName",
    display_name: "displayName",
    email: "email",
    phone: "phone",
    groups: "groups",
    birthday: "birthday",
};
const HEADER_ALIAS_TO_TARGET = {
    "first name": "firstName",
    firstname: "firstName",
    "given name": "firstName",
    "שם פרטי": "firstName",
    "last name": "lastName",
    lastname: "lastName",
    surname: "lastName",
    "family name": "lastName",
    "שם משפחה": "lastName",
    "display name": "displayName",
    "full name": "displayName",
    name: "displayName",
    "שם תצוגה": "displayName",
    email: "email",
    "e mail": "email",
    "email address": "email",
    mail: "email",
    דואל: "email",
    אימייל: "email",
    phone: "phone",
    "phone number": "phone",
    mobile: "phone",
    "mobile number": "phone",
    cell: "phone",
    "cell phone": "phone",
    cellphone: "phone",
    telephone: "phone",
    טלפון: "phone",
    "מספר טלפון": "phone",
    נייד: "phone",
    birthday: "birthday",
    "birth date": "birthday",
    birthdate: "birthday",
    "date of birth": "birthday",
    dob: "birthday",
    "תאריך לידה": "birthday",
    "יום הולדת": "birthday",
    groups: "groups",
    group: "groups",
    קבוצות: "groups",
    קבוצה: "groups",
};
export function contactImportSourceLabel(header, index) {
    const trimmed = header.trim();
    return trimmed.length > 0 ? trimmed : `Column ${index + 1}`;
}
export function normalizeContactImportHeader(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/["'`]/g, "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
}
export function suggestContactImportTarget(header) {
    const normalized = normalizeContactImportHeader(header);
    if (!normalized)
        return null;
    return HEADER_ALIAS_TO_TARGET[normalized] ?? null;
}
export function suggestContactImportMapping(headers) {
    const used = new Set();
    return headers.map((header, index) => {
        const source = contactImportSourceLabel(header, index);
        const suggested = suggestContactImportTarget(header);
        if (!suggested || used.has(suggested)) {
            return { source, target: "skip" };
        }
        used.add(suggested);
        return { source, target: suggested };
    });
}
export function contactImportMappingHasTarget(mapping) {
    return mapping.some((entry) => entry.target !== "skip");
}
export function parseContactImportCsv(csv, options) {
    const maxDataRows = options?.maxDataRows ?? CONTACT_IMPORT_MAX_DATA_ROWS;
    const rows = parseCsv(csv.replace(/^\uFEFF/, ""));
    if (rows.length === 0 || rows.every((row) => row.every((cell) => cell.trim().length === 0))) {
        throw new ContactImportParseError("EMPTY_CSV", "CSV is empty");
    }
    const headers = rows[0];
    const seen = new Set();
    for (let index = 0; index < headers.length; index++) {
        const key = contactImportSourceLabel(headers[index], index).toLowerCase();
        if (seen.has(key)) {
            throw new ContactImportParseError("DUPLICATE_HEADERS", "CSV contains duplicate column headers");
        }
        seen.add(key);
    }
    const dataRows = rows.slice(1);
    if (dataRows.length > maxDataRows) {
        throw new ContactImportParseError("TOO_MANY_ROWS", `CSV cannot contain more than ${maxDataRows} data rows`);
    }
    return { headers, dataRows };
}
export function canonicalContactImportMapping(headers) {
    const normalized = headers.map((header) => header.trim().toLowerCase());
    const missing = CONTACT_IMPORT_CSV_HEADERS.filter((header) => !normalized.includes(header));
    if (missing.length > 0)
        return null;
    const present = [
        ...CONTACT_IMPORT_CSV_HEADERS,
        ...CONTACT_IMPORT_OPTIONAL_CSV_HEADERS.filter((header) => normalized.includes(header)),
    ];
    return present.map((canonical) => {
        const index = normalized.indexOf(canonical);
        return {
            source: contactImportSourceLabel(headers[index], index),
            target: CANONICAL_HEADER_TO_TARGET[canonical],
        };
    });
}
export function assertContactImportMappingValid(headers, mapping) {
    const sourceKeys = headers.map((header, index) => contactImportSourceLabel(header, index));
    const sourceSet = new Set(sourceKeys);
    const seenSources = new Set();
    const seenTargets = new Set();
    let mappedCount = 0;
    for (const entry of mapping) {
        if (!sourceSet.has(entry.source)) {
            throw new ContactImportParseError("INVALID_MAPPING", "Mapping refers to an unknown column");
        }
        if (seenSources.has(entry.source)) {
            throw new ContactImportParseError("INVALID_MAPPING", "Each source column may be mapped only once");
        }
        seenSources.add(entry.source);
        if (entry.target === "skip")
            continue;
        if (seenTargets.has(entry.target)) {
            throw new ContactImportParseError("INVALID_MAPPING", "Each contact field may be mapped only once");
        }
        seenTargets.add(entry.target);
        mappedCount += 1;
    }
    if (mappedCount === 0) {
        throw new ContactImportParseError("INVALID_MAPPING", "Map at least one column to import");
    }
}
export function resolveContactImportMapping(headers, mapping) {
    if (mapping && mapping.length > 0) {
        assertContactImportMappingValid(headers, mapping);
        return mapping;
    }
    const canonical = canonicalContactImportMapping(headers);
    if (canonical)
        return canonical;
    throw new ContactImportParseError("MAPPING_REQUIRED", "CSV column mapping is required");
}
export function mapContactImportDataRows(headers, dataRows, mapping) {
    const sourceKeys = headers.map((header, index) => contactImportSourceLabel(header, index));
    const indexBySource = new Map(sourceKeys.map((source, index) => [source, index]));
    const indexByTarget = new Map();
    for (const entry of mapping) {
        if (entry.target === "skip")
            continue;
        const index = indexBySource.get(entry.source);
        if (index === undefined) {
            throw new ContactImportParseError("INVALID_MAPPING", "Mapping refers to an unknown column");
        }
        indexByTarget.set(entry.target, index);
    }
    const cell = (line, target) => {
        const index = indexByTarget.get(target);
        if (index === undefined)
            return "";
        return line[index]?.trim() ?? "";
    };
    return dataRows.map((line, index) => {
        const emailRaw = cell(line, "email");
        return {
            rowNum: index + 2,
            firstName: cell(line, "firstName") || undefined,
            lastName: cell(line, "lastName") || undefined,
            displayName: cell(line, "displayName") || undefined,
            email: emailRaw.length > 0 ? emailRaw : undefined,
            phoneRaw: cell(line, "phone"),
            birthdayRaw: cell(line, "birthday"),
            groupsCell: cell(line, "groups"),
        };
    });
}
export function parseAndMapContactImportRows(csv, mapping, options) {
    const { headers, dataRows } = parseContactImportCsv(csv, options);
    const resolved = resolveContactImportMapping(headers, mapping);
    return mapContactImportDataRows(headers, dataRows, resolved);
}
export function isContactImportParseError(error) {
    return error instanceof ContactImportParseError;
}
//# sourceMappingURL=contact-import-mapping.js.map